import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { doc, getDoc, setDoc, Timestamp } from "firebase/firestore";
import { defaultItems, AUTO_SOURCES } from "../utils/payroll";

// 給与項目の設定。金額の入力画面とは分けてある。
// key は内部用の不変の識別子で、名前を変えても過去の給与記録が壊れないようにしている。

function PayrollItems({ companyId, user, onBack }) {
  const [items, setItems]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);
  const [error, setError]   = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "payrollSettings", companyId));
        const list = snap.exists() && Array.isArray(snap.data().items) && snap.data().items.length > 0
          ? snap.data().items
          : defaultItems();
        if (alive) setItems(list);
      } catch (e) {
        console.error("項目設定の取得に失敗しました:", e);
        if (alive) { setItems(defaultItems()); setError("設定の読み込みに失敗しました。"); }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [companyId]);

  const update = (i, key, value) => setItems(list => list.map((it, idx) => idx === i ? { ...it, [key]: value } : it));

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    setItems(next);
  };

  const addItem = (type) => {
    const key = `custom_${Date.now()}`;
    setItems([...items, { key, name: "", type, auto: "" }]);
  };

  const removeItem = (i) => {
    const it = items[i];
    if (!window.confirm(`「${it.name || "（名称未入力）"}」を削除しますか？\n\n既に保存済みの給与に入っている金額は残りますが、今後の計算では使われなくなります。`)) return;
    setItems(items.filter((_, idx) => idx !== i));
  };

  const save = async () => {
    setError(""); setSaved(false);
    if (items.some(it => !it.name.trim())) { setError("名称が空の項目があります。"); return; }
    const names = items.map(it => it.name.trim());
    if (new Set(names).size !== names.length) { setError("同じ名称の項目があります。"); return; }

    setSaving(true);
    try {
      await setDoc(doc(db, "payrollSettings", companyId), {
        companyId,
        items: items.map(it => ({ ...it, name: it.name.trim() })),
        itemsUpdatedAt: Timestamp.now(),
        itemsUpdatedBy: user.email,
      }, { merge: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      console.error("項目設定の保存に失敗しました:", e);
      setError("保存に失敗しました。権限をご確認ください。");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={st.container}><p style={{ color: "#999" }}>読み込み中...</p></div>;

  const section = (type) => {
    const list = items.map((it, i) => ({ it, i })).filter(x => x.it.type === type);
    return (
      <>
        <div style={st.section}>{type}項目</div>
        {list.length === 0 && <p style={st.empty}>項目がありません</p>}
        {list.map(({ it, i }) => (
          <div key={it.key} style={st.row}>
            <div style={st.orderBtns}>
              <button style={st.orderBtn} onClick={() => move(i, -1)} disabled={i === 0}>▲</button>
              <button style={st.orderBtn} onClick={() => move(i, 1)} disabled={i === items.length - 1}>▼</button>
            </div>
            <input style={st.nameInput} value={it.name} placeholder="項目名（例：役職手当）"
              onChange={e => update(i, "name", e.target.value)} />
            <select style={st.select} value={it.auto || ""} onChange={e => update(i, "auto", e.target.value)}>
              {AUTO_SOURCES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <button style={st.delBtn} onClick={() => removeItem(i)}>削除</button>
          </div>
        ))}
        <button style={st.addBtn} onClick={() => addItem(type)}>＋ {type}項目を追加</button>
      </>
    );
  };

  return (
    <div style={st.container}>
      <button style={st.back} onClick={onBack}>← 給与計算に戻る</button>
      <h2 style={st.title}>📋 項目設定</h2>
      <p style={st.lead}>
        給与計算の表に並ぶ項目です。名称の変更・並べ替え・追加・削除ができます。
        「自動計算」を割り当てた項目は計算結果が入り、「手入力」の項目は空欄のままなので表で直接入力してください。
      </p>

      <div style={st.hintBox}>
        名称を変えても、過去に確定した給与の記録はそのときの名称のまま残ります。
        金額の入力はこの画面ではなく「計算」タブで行ってください。
      </div>

      {section("支給")}
      {section("控除")}

      {error && <p style={st.error}>{error}</p>}

      <div style={st.btnRow}>
        <button style={st.saveBtn} onClick={save} disabled={saving}>{saving ? "保存中..." : "保存する"}</button>
        {saved && <span style={{ color: "#27ae60", alignSelf: "center" }}>✅ 保存しました</span>}
      </div>
    </div>
  );
}

const st = {
  container: { padding: "32px", maxWidth: "820px", margin: "0 auto" },
  back: { background: "none", border: "none", color: "#2980b9", cursor: "pointer", fontSize: "14px", padding: 0, marginBottom: "12px" },
  title: { color: "#2c3e50", marginBottom: "6px" },
  lead: { color: "#7f8c8d", fontSize: "14px", lineHeight: "1.8", marginTop: 0, marginBottom: "16px" },
  hintBox: { backgroundColor: "#eef2f6", borderRadius: "10px", padding: "12px 16px", fontSize: "13px", color: "#5d6d7e", lineHeight: "1.8", marginBottom: "8px" },
  section: { color: "#2c3e50", fontWeight: "bold", fontSize: "15px", margin: "26px 0 12px", paddingBottom: "6px", borderBottom: "2px solid #f0f4f8" },
  row: { display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px", flexWrap: "wrap" },
  orderBtns: { display: "flex", flexDirection: "column", gap: "2px" },
  orderBtn: { width: "26px", height: "18px", padding: 0, fontSize: "10px", border: "1px solid #ddd", backgroundColor: "#fff", borderRadius: "4px", cursor: "pointer", lineHeight: 1 },
  nameInput: { flex: "1 1 180px", minWidth: "150px", padding: "9px 12px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "14px" },
  select: { flex: "1 1 220px", minWidth: "200px", padding: "9px 10px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "13px", backgroundColor: "#fff" },
  delBtn: { padding: "8px 14px", backgroundColor: "#fff", color: "#e74c3c", border: "1px solid #e74c3c", borderRadius: "8px", cursor: "pointer", fontSize: "13px" },
  addBtn: { marginTop: "6px", padding: "9px 18px", backgroundColor: "#fff", color: "#2c3e50", border: "1px dashed #99a", borderRadius: "8px", cursor: "pointer", fontSize: "13px" },
  empty: { color: "#999", fontSize: "13px" },
  error: { color: "#e74c3c", fontSize: "14px", marginTop: "14px" },
  btnRow: { display: "flex", gap: "12px", marginTop: "28px" },
  saveBtn: { padding: "12px 32px", backgroundColor: "#2c3e50", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "15px" },
};

export default PayrollItems;
